import { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PlanoAcaoCard } from './PlanoAcaoCard';
import { PlanoAcaoComDetalhes } from '@/hooks/usePlanoAcaoResponsavel';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PlanosKanbanProps {
  planos: PlanoAcaoComDetalhes[];
  onPlanoClick: (plano: PlanoAcaoComDetalhes) => void;
  calcularProgressoPlano: (plano: PlanoAcaoComDetalhes) => number;
  onRefetch: () => void;
}

const KANBAN_COLUMNS = [
  { id: 'aberto', label: 'Aberto', color: 'bg-blue-50 dark:bg-blue-950/30' },
  { id: 'em_andamento', label: 'Em Andamento', color: 'bg-yellow-50 dark:bg-yellow-950/30' },
  { id: 'concluido', label: 'Concluído', color: 'bg-green-50 dark:bg-green-950/30' },
  { id: 'cancelado', label: 'Cancelado', color: 'bg-gray-50 dark:bg-gray-950/30' },
];

// Sortable Plano Card Wrapper
const SortablePlanoCard = ({ 
  plano, 
  onClick, 
  calcularProgressoPlano 
}: { 
  plano: PlanoAcaoComDetalhes; 
  onClick: () => void;
  calcularProgressoPlano: (plano: PlanoAcaoComDetalhes) => number;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: plano.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
      className="touch-manipulation"
    >
      <PlanoAcaoCard
        plano={plano}
        onClick={onClick}
        calcularProgressoPlano={calcularProgressoPlano}
        variant="compact"
      />
    </div>
  );
};

// Droppable Column
const KanbanColumn = ({ 
  column, 
  planos, 
  onPlanoClick, 
  calcularProgressoPlano,
}: { 
  column: typeof KANBAN_COLUMNS[0];
  planos: PlanoAcaoComDetalhes[];
  onPlanoClick: (plano: PlanoAcaoComDetalhes) => void;
  calcularProgressoPlano: (plano: PlanoAcaoComDetalhes) => number;
}) => {
  const planoIds = planos.map(p => p.id);

  return (
    <Card className={cn("flex flex-col min-w-[280px] max-w-[320px] flex-shrink-0", column.color)}>
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-medium">{column.label}</CardTitle>
            <Badge variant="secondary" className="h-5 px-1.5">
              {planos.length}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-2 pt-0">
        <ScrollArea className="h-[calc(100vh-22rem)]">
          <SortableContext items={planoIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-2 pr-2">
              {planos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Nenhum plano
                </div>
              ) : (
                planos.map((plano) => (
                  <SortablePlanoCard
                    key={plano.id}
                    plano={plano}
                    onClick={() => onPlanoClick(plano)}
                    calcularProgressoPlano={calcularProgressoPlano}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export const PlanosKanban = ({ 
  planos, 
  onPlanoClick, 
  calcularProgressoPlano,
  onRefetch,
}: PlanosKanbanProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group planos by status
  const planosPorStatus = useMemo(() => {
    const grouped: Record<string, PlanoAcaoComDetalhes[]> = {
      aberto: [],
      em_andamento: [],
      concluido: [],
      cancelado: [],
    };

    planos.forEach((plano) => {
      const status = plano.status || 'aberto';
      if (grouped[status]) {
        grouped[status].push(plano);
      } else {
        grouped.aberto.push(plano);
      }
    });

    return grouped;
  }, [planos]);

  const activePlano = activeId ? planos.find(p => p.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activePlano = planos.find(p => p.id === active.id);
    if (!activePlano) return;

    // Find which column the item was dropped into
    const overId = over.id as string;
    
    // Check if dropped over a column header
    const targetColumn = KANBAN_COLUMNS.find(col => col.id === overId);
    let newStatus: string | null = null;

    if (targetColumn && targetColumn.id !== activePlano.status) {
      newStatus = targetColumn.id;
    } else {
      // Check if dropped over another card
      const overPlano = planos.find(p => p.id === overId);
      if (overPlano && overPlano.status !== activePlano.status) {
        newStatus = overPlano.status;
      }
    }

    if (newStatus) {
      try {
        const { error } = await supabase
          .from('formulario_planos_acao')
          .update({ 
            status: newStatus,
            atualizado_em: new Date().toISOString(),
            ...(newStatus === 'concluido' ? { concluido_em: new Date().toISOString() } : {})
          })
          .eq('id', activePlano.id);

        if (error) throw error;
        
        toast.success('Status atualizado com sucesso');
        onRefetch();
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
        toast.error('Erro ao atualizar status do plano');
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            planos={planosPorStatus[column.id] || []}
            onPlanoClick={onPlanoClick}
            calcularProgressoPlano={calcularProgressoPlano}
          />
        ))}
      </div>

      <DragOverlay>
        {activePlano && (
          <PlanoAcaoCard
            plano={activePlano}
            calcularProgressoPlano={calcularProgressoPlano}
            variant="compact"
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default PlanosKanban;
